// MeetJohnIntro — short, tight lead-in to John's full demo.
// Replaces the long 26-chapter JohnTillyJourney block (per Glenn, Feb 2026).
// Story arc: who John is → diagnosis → struggle → CTA to watch the demo.
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Play } from "@phosphor-icons/react";
import { ForgetMeNotIcon } from "../ForgetMeNotIcon";

const FADE = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-100px" },
    transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] },
};

const JOHN_IMG = "https://static.prod-images.emergentagent.com/jobs/423db95f-0a97-4ed9-8976-baf5967093c6/images/99b1413d5272171dee30c114363cc09423048cfac201708f65fa0b33a3471f58.png";

export function MeetJohnIntro() {
    return (
        <section className="relative py-28 px-6 rem-watercolor-bg" data-testid="meet-john" id="john">
            <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
                <motion.div {...FADE} className="lg:col-span-5">
                    <div className="rounded-3xl overflow-hidden shadow-rem-card aspect-[4/5] bg-[#E8E3DA]">
                        <img src={JOHN_IMG} alt="John Tilly — 70, husband, father, grandfather, veteran" className="w-full h-full object-cover" data-testid="john-portrait" />
                    </div>
                    <div className="absolute -bottom-6 -right-6 hidden md:block"><ForgetMeNotIcon size={72} /></div>
                </motion.div>

                <motion.div {...FADE} transition={{ ...FADE.transition, delay: 0.12 }} className="lg:col-span-7">
                    <p className="font-sans text-xs uppercase tracking-[0.3em] text-[#6B7DB8] font-semibold mb-6">
                        Meet John
                    </p>
                    <h2 className="font-serif text-4xl sm:text-5xl text-[#2D2B2A] leading-tight">
                        John is 70.<br/>
                        <em className="italic text-[#4A5A95]">A husband, father, grandfather, mate — and an army veteran.</em>
                    </h2>

                    <div className="mt-8 space-y-5 text-[#5C5A59] text-lg leading-relaxed font-light max-w-prose">
                        <p>
                            He's lived a full, happy life.
                        </p>
                        <p>
                            Eight years ago John was diagnosed with early-onset dementia. Over time it has progressed to full onset, and he now struggles to stay connected with his family, his friends and his own past — fighting the cruel slow erasure that dementia performs on memory and recall.
                        </p>
                        <p className="text-[#2D2B2A] font-serif italic text-xl text-balance">
                            Watch John's journey, narrated by the family who built it for him — to pull those memories back, one chapter at a time.
                        </p>
                    </div>

                    <div className="mt-10">
                        <Link
                            to="/demo/john"
                            className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-[#6B7DB8] hover:bg-[#4A5A95] text-white font-medium text-base transition-all shadow-rem-card hover:shadow-lg hover:scale-[1.02]"
                            data-testid="meet-john-cta"
                        >
                            <Play size={18} weight="fill" />
                            Watch John's session  ·  6 min
                        </Link>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
